import Vue from 'vue';
import {
  alignForNumber,
  addGroupingInResponseData,
  LinkCellRenderer,
  autoResize,
  getPinnedRowData,
  getNodeChildDetails,
  formatNumber, alignCells, MultiLinkCellRenderer
} from '@/helpers/renderHelpers';
import {
  SumRows,
  handlerResponse, addColIdToColumns, convertTotalDataToObject
} from '@/helpers/commonHelpers';
import gridProps from '@/gridProps';
import { PURCHASE, SALE_AGENT } from '@/constants/invoiceTypes';
import { existenceAutoCreated } from '@/helpers/sale-invoices-helpers';

const cellStyle = (params) => {
  return alignForNumber(params.value);
}
export default {
  namespaced: true,
  state: {
    agentCommissions: [],
    agentCommissionsCount: 0,
    agentCommissionsColumns: [],
    agentCommissionsPinnedRowData: [],
    salesAgentCommissionFeesBindingList: [],
    salesAgentCommissionFeesBindingColumns: [],
    salesAgentCommissionFeesBindingCount: 0,
    salesAgentCommissionFeesBindingPinnedRowData: [],
    salesAgentCommissionFeesList: [],
    purchaseAgentCommissionFeesList: [],
    invoiceMarginList: [],
    salesAgentCommissionFeesCount: 0,
    purchaseAgentCommissionFeesCount: 0,
    commissionFeesListColumns: [
      {field: 'group', colId: 'group', width: 80, headerName: '', checkboxSelection: true},
      {field: 'buyer_name', colId: 'buyer_name', headerName: 'Buyer', cellRenderer: 'BuyerLink'},
      {field: 'commission_type_display', colId: 'commission_type_display', headerName: 'Comm. type'},
      {field: 'invoice_number', colId: 'invoice_number', headerName: 'Invoice No.', cellRenderer: 'InvoiceLink'},
      {field: 'invoice_date', colId: 'invoice_date', headerName: 'Sales invoice date'},
      {field: 'value', colId: 'value', headerName: 'Commission Value'},
      {field: 'sum', colId: 'sum', headerName: 'Commission sum'},
      {field: 'currency_code', colId: 'currency_code', headerName: 'Commission Currency'}
    ],
    salesCommissionFeesColumns: [],
    purchaseCommissionFeesColumns: [],
    commissionFees: [],
    salesCommissionFeesPinnedRowData: [],
    purchaseCommissionFeesPinnedRowData: [],
    invoiceMarginColumns: [],
    invoiceMarginPinnedRowData: [],
    invoiceMarginReports: [],
    paymentAndInvoices: [],
    paymentAndInvoicesColumns: [],
    paymentAndInvoicesReports: [],
    impactTypes: [],
    gridOptions: {
      animateRows: true,
      rowSelection: 'multiple',
      rowGroupPanelShow: 'always',
      aggFuncs: {
        'SumRows': SumRows
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        cellStyle
      },
      components: {
        'SellerLink': LinkCellRenderer('seller_id', '/partners/'),
        'AgentLink': LinkCellRenderer('agent_id', '/partners/'),
        'BuyerLink': LinkCellRenderer('buyer_id', '/partners/'),
        'SupplierLink': LinkCellRenderer('supplier_id', '/partners/'),
        'InvoiceLink': LinkCellRenderer('invoice_id', '/sales-invoice/'),
        'AgentInvoiceLink': LinkCellRenderer('sale_agent_invoice_id', '/sales-agent-invoices/'),
        'PurchaseInvoiceLink': LinkCellRenderer('purchase_invoice_id', '/purchase-invoice/')
      },
      onRowDataChanged() {
        if (this.rowData && this.rowData.length) {
          autoResize(this);
        } else {
          this.api && this.api.hideOverlay();
          this.api && this.api.showNoRowsOverlay();
        }
      }
    },
    salesInvoicesTypes: [],
    gridName: '',
    salesInvoicesExports: [],
    independent_si_types: [],
    si_types: []
  },
  getters: {
    salesAgentCommissionFeesBindingList(state) {
      return state.salesAgentCommissionFeesBindingList;
    },
    salesAgentCommissionFeesList(state) {
      return state.salesAgentCommissionFeesList;
    },
    purchaseAgentCommissionFeesList(state) {
      return state.purchaseAgentCommissionFeesList;
    },
    salesAgentCommissionFeesCount(state) {
      return state.salesAgentCommissionFeesCount;
    },
    salesCommissionFeesColumns(state) {
      return state.salesCommissionFeesColumns;
    },
    commissionFees(state) {
      return state.commissionFees;
    },
    agentCommissionsGrid(state) {
      return {
        ...state.gridOptions,
        rowGroupPanelShow: 'none',
        components: {
          'AgentLink': LinkCellRenderer('agent_id', '/partners/'),
          'CustomerLink': LinkCellRenderer('customer_id', '/partners/'),
          'SupplierLink': LinkCellRenderer('supplier_id', '/partners/'),
        },
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true,
          valueFormatter(params) {
            return formatNumber(params.value, null)
          },
          cellStyle(params) {
            return alignForNumber(params.value);
          }
        },
      }
    },
    salesAgentCommissionsGrid(state) {
      return {
        ...state.gridOptions,
        rowGroupPanelShow: 'none',
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true,
          valueFormatter(params) {
            return formatNumber(params.value, null)
          },
          cellStyle(params) {
            return alignForNumber(params.value);
          }
        },
      }
    },
    commissionFeesGridOptions(state) {
      return {
        ...state.gridOptions,
        ...gridProps,
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true,
          valueFormatter(params) {
            const colId = params.column.colId;
            return colId.includes('m3')
                ? formatNumber(params.value, 3)
                : formatNumber(params.value, 2)
          },
          cellStyle
        }
      };
    },
    invoiceMarginGridOptions(state) {
      return {
        ...state.gridOptions,
        rowGroupPanelShow: 'none',
        components: {
          'BuyerLink': LinkCellRenderer('invoice_buyer_id', '/partners/'),
          'InvoiceLink': LinkCellRenderer('invoice_id', '/sales-invoice/', true)
        },
        defaultColDef: {
          valueFormatter(params) {
            return params.column.colId.includes('m3') || params.column.colId.includes('margin_percent') ? formatNumber(params.value, 3) :
              params.column.colId.includes('margin') ? formatNumber(params.value, 2) : params.value
          },
          cellStyle
        },
        getNodeChildDetails: getNodeChildDetails
      }
    },
    paymentAndInvoicesGridOptions(state) {
      return {
        ...state.gridOptions,
        getNodeChildDetails: getNodeChildDetails,
        rowGroupPanelShow: 'none',
        defaultColDef: {
          ...state.gridOptions.defaultColDef,
          valueFormatter(params) {
            return formatNumber(params.value);
          },
          cellStyle
        },
        components: {
          'InvoiceLink': LinkCellRenderer('invoice_id', '/sales-invoice/', true),
          'SalesAgentInvoiceLink': LinkCellRenderer('invoice_id', '/sales-agent-invoices/'),
          'PaymentLink': LinkCellRenderer('payment_id', '/payments/'),
          'PurchaseInvoiceLink': LinkCellRenderer('invoice_id', '/purchase-invoice/')
        },
      }
    },
    commissionFeesListGridOptions(state) {
      return {...state.gridOptions, rowGroupPanelShow: 'never'};
    },
  },
  mutations: {
    setSaleAgentCommissionsBindingList(state, data) {
      state.salesAgentCommissionFeesBindingList = data.data.results;
      state.salesAgentCommissionFeesBindingCount = data.data.count;
      state.salesAgentCommissionFeesBindingColumns = addColIdToColumns(data.used_columns);
      state.salesAgentCommissionFeesBindingPinnedRowData= getPinnedRowData(data.total, 'name', 'value');
    },
    setSaleAgentCommissionFeesTotal(state, data) {
      if (data.data) {
        state.salesCommissionFeesPinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.pinnedRowData = data;
      }
      if (data.grid_name) {
        state.gridName = data.grid_name;
      }
    },
    setSaleAgentCommissionFeesList(state, data) {
      if (data.data) {
        state.salesAgentCommissionFeesList = data.data.results;
        state.salesAgentCommissionFeesCount = data.data.count;
        state.salesCommissionFeesColumns = addColIdToColumns(data.used_columns);
        state.salesCommissionFeesPinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.salesAgentCommissionFeesList = data;
        state.pinnedRowData = data;
        state.salesAgentCommissionFeesCount = 0;
      }
      if (data.grid_name) {
        state.gridName = data.grid_name;
      }
    },
    setPurchaseAgentCommissionFeesList(state, data) {
      if (data.data) {
        state.purchaseAgentCommissionFeesList = data.data.results;
        state.purchaseAgentCommissionFeesCount = data.data.count;
        state.purchaseCommissionFeesColumns = addColIdToColumns(data.used_columns);
        state.purchaseCommissionFeesPinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.purchaseAgentCommissionFeesList = data;
        state.pinnedRowData = data;
        state.purchaseAgentCommissionFeesCount = 0;
      }
      if (data.grid_name) {
        state.gridName = data.grid_name;
      }
    },
    setAgentCommissionsList(state, data) {
      state.agentCommissions = data.data.results.map(item => {
        if (item.trees) {
          item.trees_str = item.trees.map(tree => tree.name).toString();
        }
        return item;
      });
      state.agentsCommissionsCount =  data.data.count;
      state.agentCommissionsColumns = addColIdToColumns(data.used_columns);
      state.agentCommissionsPinnedRowData = getPinnedRowData(data.total, 'name', 'value');
    },
    setSalesInvoicesTypes(state, data) {
      state.salesInvoicesTypes = data;
    },
    setInvoiceMargin(state, data) {
      if (data.data) {
        state.invoiceMarginList = addGroupingInResponseData(data.data);
        state.invoiceMarginColumns = addColIdToColumns(data.used_columns);
        state.invoiceMarginPinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.invoiceMarginList = data;
        state.invoiceMarginPinnedRowData = data;
      }
    },
    setInvoiceMarginReports(state, data) {
      state.invoiceMarginReports = data;
    },
    setPaymentsAndInvoicesList(state, data) {
      if (data.data) {
        state.paymentAndInvoices = addGroupingInResponseData(data.data);
        state.paymentAndInvoicesColumns = addColIdToColumns(data.used_columns).map(col => {
          if (col.field === 'invoice_number') {
            col.cellRendererSelector = (params) => {
              const invoiceType = params.data.invoice_type;
              if (invoiceType === SALE_AGENT) {
                return {component: 'SalesAgentInvoiceLink'};
              } else if (invoiceType === PURCHASE) {
                return {component: 'PurchaseInvoiceLink'};
              } else {
                return {component: 'InvoiceLink'};
              }
            }
          }
          return col
        });
      } else {
        state.paymentAndInvoices = data;
      }
    },
    setPaymentAndInvoicesReports(state, data) {
      state.paymentAndInvoicesReports = data;
    },
    setImpactTypes(state, data) {
      state.impactTypes = data;
    },
    setSalesInvoicesExports(state, payload) {
      state.salesInvoicesExports = payload;
    },
    setIndependentSaleInvoicesTypes(state, payload) {
      state.independent_si_types = payload;
    },
    setSaleInvoicesTypes(state, payload) {
      state.si_types = payload;
    },
  },
  actions: {
    loadSaleAgentCommissionsBindingList(store, payLoad) {
      Vue.http.get('accounting/sale_agent_commission_fees/bind_with_sa_invoice/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setSaleAgentCommissionsBindingList', data);
        });
    },
    updateTotalDataForIds(store, payLoad) {
      if (store.getters.commissionFeesGridOptions.api) {
        store.getters.commissionFeesGridOptions.api.showLoadingOverlay();
      }
      Vue.http.get('accounting/sale_agent_commission_fees/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setSaleAgentCommissionFeesTotal', data);
        });
    },
    loadSaleAgentCommissionsFeesList(store, payLoad) {
      if (store.getters.commissionFeesGridOptions.api) {
        store.getters.commissionFeesGridOptions.api.showLoadingOverlay();
      }
      Vue.http.get('accounting/sale_agent_commission_fees/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setSaleAgentCommissionFeesList', data);
        });
    },
    loadPurchaseAgentCommissionsFeesList(store, payLoad) {
      if (store.getters.commissionFeesGridOptions.api) {
        store.getters.commissionFeesGridOptions.api.showLoadingOverlay();
      }
      Vue.http.get('accounting/purchase_agent_commission_fees/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setPurchaseAgentCommissionFeesList', data);
        });
    },
    loadSaleAgentCommissionsList(store, payLoad) {
      Vue.http.get('accounting/sale_agent_commissions/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setAgentCommissionsList', data);
        });
    },
    loadPurchaseAgentCommissionsList(store, payLoad) {
      Vue.http.get('accounting/purchase_agent_commissions/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setAgentCommissionsList', data);
        });
    },
    loadSalesInvoicesTypes(store, payLoad) {
      Vue.http.get(`accounting/sale_invoice_types/`, {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setSalesInvoicesTypes', data);
        });
    },
    loadInvoiceMarginList(store, payLoad) {
      Vue.http.get(`accounting/invoice_margin_records_diff/`, {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setInvoiceMargin', data);
        });
    },
    loadInvoiceMarginReports(store) {
      Vue.http.get(`accounting/invoice_margin_reports/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setInvoiceMarginReports', data);
        });
    },
    loadPaymentsAndInvoicesList(store, payLoad) {
      Vue.http.get(`accounting/invoice_payment_transfers/`, {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setPaymentsAndInvoicesList', data);
        });
    },
    loadPaymentAndInvoicesReports(store) {
      Vue.http.get(`accounting/invoice_payment_reports/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setPaymentAndInvoicesReports', data);
        });
    },
    loadImpactTypes(store) {
      Vue.http.get(`accounting/invoice_margin_records_impact_types/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setImpactTypes', data);
        });
    },
    loadSalesInvoicesExports(store) {
      Vue.http.get('accounting/invoice_reports/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setSalesInvoicesExports', data);
        });
    },
    loadIndependentSaleInvoicesTypes(store) {
      Vue.http.get('accounting/sale_invoice_types/?independent_invoice=true&invoice_for_pair=true')
        .then(handlerResponse)
        .then(data => {
          store.commit('setIndependentSaleInvoicesTypes', data);
        });
    },
    loadSaleInvoicesTypes(store) {
      Vue.http.get('accounting/sale_invoice_types/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setSaleInvoicesTypes', data);
        });
    },
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/accounting.js